import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiX, FiLayers, FiSend, FiCheckCircle } = FiIcons;

const BulkOrderModal = ({ isOpen, onClose }) => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', eventDate: '', details: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSending(true);
    setTimeout(() => {
      setSending(false);
      setSent(true);
      setTimeout(() => {
        setSent(false);
        setForm({ name: '', email: '', phone: '', eventDate: '', details: '' });
        onClose();
      }, 2000);
    }, 1500);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      >
        <motion.div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} />

        <motion.div
          className="relative w-full max-w-md bg-white rounded-[32px] shadow-2xl overflow-hidden"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        >
          {/* Header */}
          <div className="bg-accent-navy px-6 py-6 text-white relative">
            <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors">
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-accent-gold/20 border border-accent-gold/30 rounded-2xl flex items-center justify-center">
                <SafeIcon icon={FiLayers} className="w-6 h-6 text-accent-gold" />
              </div>
              <div>
                <h3 className="font-serif text-xl">Bulk Order Quote</h3>
                <p className="text-[10px] font-black uppercase tracking-widest text-white/60 mt-1">
                  Volume Pricing Request
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {sent ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center justify-center py-8 text-center"
              >
                <div className="w-16 h-16 bg-green-50 text-green-500 rounded-full flex items-center justify-center mb-4">
                  <SafeIcon icon={FiCheckCircle} className="w-8 h-8" />
                </div>
                <h4 className="font-bold text-accent-navy text-lg mb-2">Request Sent Successfully</h4>
                <p className="text-sm text-text-muted font-light max-w-[250px]">
                  Our team will review your request and contact you within 24 hours with a custom quote.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <p className="text-xs text-text-muted font-light mb-2">
                  Please provide details about your memorial event and the items you need.
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Full Name</label>
                    <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                      className="w-full px-4 py-2.5 bg-brand-light border-2 border-brand-dark rounded-xl focus:border-accent-gold outline-none text-sm transition-colors" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Event Date</label>
                    <input type="date" required value={form.eventDate} onChange={e => setForm({...form, eventDate: e.target.value})}
                      className="w-full px-4 py-2.5 bg-brand-light border-2 border-brand-dark rounded-xl focus:border-accent-gold outline-none text-sm transition-colors" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Email Address</label>
                    <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                      className="w-full px-4 py-2.5 bg-brand-light border-2 border-brand-dark rounded-xl focus:border-accent-gold outline-none text-sm transition-colors" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Phone Number</label>
                    <input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                      className="w-full px-4 py-2.5 bg-brand-light border-2 border-brand-dark rounded-xl focus:border-accent-gold outline-none text-sm transition-colors" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Order Details & Quantities</label>
                  <textarea required rows={4} value={form.details} onChange={e => setForm({...form, details: e.target.value})}
                    placeholder="e.g., I need 150 custom silk scarves and 50 candle sets..."
                    className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-xl focus:border-accent-gold outline-none text-sm resize-none transition-colors" />
                </div>

                <button type="submit" disabled={sending}
                  className="w-full py-3 mt-2 bg-accent-navy text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all flex items-center justify-center gap-2 disabled:opacity-70">
                  {sending ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <SafeIcon icon={FiSend} className="w-4 h-4" />
                  )}
                  {sending ? 'Sending Request...' : 'Submit Request'}
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BulkOrderModal;